/* eslint-disable @typescript-eslint/no-restricted-imports */
import {
  AlertActionButton,
  AlertDismissButton,
  SystemAlertHeader,
} from '@components/SystemAlerts/shared/components';
import {
  AlertTypeProps,
  SystemAlertType,
} from '@components/SystemAlerts/shared/constants';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { FS_UNMASK } from '@utils/fullstory';
import { toast } from 'react-toastify';
import { SystemBannerOpts } from '..';

const BannerContainer = styled.div({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr auto',
  alignItems: 'center',
  '@media (max-width: 720px)': {
    gridTemplateColumns: '1fr',
    gap: '16px',
  },
});

const Content = styled.div<AlertTypeProps>((props) => ({
  color: props?.color,
  marginLeft: 16,
  '@media (max-width: 720px)': {
    marginLeft: '24px',
  },
}));

const ButtonContainer = styled.div({
  marginLeft: 24,
  width: 'max-content',
});

interface SystemBannerProps {
  title?: string;
  content: Maybe<string>;
  action?: {
    label: string;
    onClick: () => void;
  };
  type: SystemAlertType;
  dismiss?: {
    label?: string;
    onClick?: () => void;
  };
  closeToast?: () => void;
  toastOptions?: SystemBannerOpts;
  unmaskTitle?: boolean;
  unmaskContent?: boolean;
}

const SystemBanner = ({
  title,
  content,
  action,
  dismiss,
  closeToast,
  type,
  unmaskTitle = false,
  unmaskContent = false,
}: SystemBannerProps): JSX.Element => {
  const { system } = useTheme();
  // SystemBanner reuses toast colors since the two have identical styling.
  const toastColors = system.toast[type];
  return (
    <BannerContainer>
      <SystemAlertHeader
        title={title}
        type={type}
        unmaskTitle={unmaskTitle}
        colors={{
          accent: toastColors.accent,
          title: toastColors.title,
        }}
      />
      <Content
        type={type}
        className={unmaskContent ? FS_UNMASK : undefined}
        color={toastColors.text}
      >
        {content}
      </Content>
      <ButtonContainer>
        {action && (
          <AlertActionButton
            action={action}
            type={type}
            closeToast={(): void => closeToast?.()}
            css={{
              background: toastColors.actionButtonBackground,
              color: toastColors.actionButtonColor,
              ':hover': {
                background: `${toastColors.actionButtonHover} !important`,
              },
            }}
          />
        )}
        <AlertDismissButton
          type={type}
          dismiss={dismiss}
          onClick={(): void => closeToast?.()}
          css={{
            marginLeft: action ? '0px' : '-12px',
            background: 'transparent',
            color: toastColors.dismissButtonColor,
            ':hover': {
              background: `${toastColors.dismissButtonHover} !important`,
            },
          }}
        />
      </ButtonContainer>
    </BannerContainer>
  );
};

// ts-unused-exports:disable-next-line
export interface SystemBannerComponentProps {
  title?: string;
  content: Maybe<string>;
  updateExisting?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
  dismiss?: {
    label?: string;
    onClick?: () => void;
  };
  type: 'success' | 'warning' | 'error' | 'info';
  systemBannerOpts?: SystemBannerOpts;
  unmaskTitle?: boolean;
  unmaskContent?: boolean;
}

export const systemBannerComponent = ({
  title,
  content,
  action,
  dismiss,
  type,
  unmaskTitle,
  unmaskContent,
  systemBannerOpts,
  updateExisting = false,
}: SystemBannerComponentProps): number | string => {
  const banner = (
    <SystemBanner
      title={title}
      content={content}
      action={action}
      dismiss={dismiss}
      type={type}
      unmaskTitle={unmaskTitle}
      unmaskContent={unmaskContent}
    />
  );
  const toastId = systemBannerOpts?.toastId;
  if (
    updateExisting &&
    toastId !== undefined &&
    toast.isActive(toastId, 'systemBanner')
  ) {
    toast.update(toastId, { render: banner, containerId: 'systemBanner' });
    return toastId;
  }

  return toast[type](banner, {
    autoClose: false,
    hideProgressBar: true,
    // We render our own type icon inside <SystemBanner>; disable
    // react-toastify's default type-based icon (v9+) to avoid a duplicate.
    icon: false,
    containerId: 'systemBanner',
    position: 'top-center',
    ...systemBannerOpts,
  });
};
